import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { siteSpecificData } from '../../../../environments/site-specific-data';
import { ApplicationState } from '../../../store/application-store';
import { Authenticate } from '../../../auth/models/authenticate';
import { NewUserConfirmationModel } from '../../models/new-user-confirmation';
import * as customFormValidators from '../../../common/validators/custom-form-validators';
import * as authActions from '../../../auth/actions/auth';
import { map, catchError, takeWhile } from 'rxjs';

const API_URL = siteSpecificData.apiUrlBase;

@Component({
  selector: 'mojo-new-user-page',
  templateUrl: './new-user-page.component.html'
})
export class NewUserPageComponent implements OnInit, OnDestroy {
  userId: number;
  name: string;
  validationToken: string;
  salutationName: string;
  passwordFieldBlurred = false;
  isAlive = true;
  newUserPasswordForm: UntypedFormGroup;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private store: Store<ApplicationState>,
    private formBuilder: UntypedFormBuilder,
    private messageService: ToastrService
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.route.queryParams.pipe(
      takeWhile(() => this.isAlive)
    ).subscribe(params => {
      this.userId = +params['userId'];
      this.name = this.urldecode(params['name']);
      this.validationToken = params['code'];
      this.salutationName = this.urldecode(params['salutationName']);
    });
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  passwordBlurred() {
    this.passwordFieldBlurred = true;
  }

  private confirmUser(confirmation: NewUserConfirmationModel) {
    const inviteUrl = `${API_URL}account/confirm`;
    try {
      this.http
        .post(inviteUrl, confirmation, { responseType: 'text' }).pipe(
          map((response) => {
            const auth: Authenticate = new Authenticate();
            auth.username = confirmation.UserName;
            auth.password = confirmation.Password;
            this.store.dispatch(new authActions.Login({ authenticate: auth, returnUrl: '/' }));
          }),
          catchError(exception => {
            console.error(exception);
            this.messageService.error('Could not confirm email: ' + exception.error);
            return of(exception);
          }),
          takeWhile(() => this.isAlive)
        ).subscribe((v) => { });
    } catch (e) {
      console.error(e);
      this.messageService.error('Could not confirm email: ' + e);
    }
  }

  createForm() {
    this.newUserPasswordForm = this.formBuilder.group({
      Passwords: this.formBuilder.group({
        Password: [
          '',
          [
            Validators.required, Validators.minLength(10), customFormValidators.containsUppercase,
            customFormValidators.containsLowercase, customFormValidators.containsDigit,
            customFormValidators.containsSymbol
          ]
        ],
        ConfirmPassword: ['',
          [Validators.required, Validators.minLength(10), customFormValidators.containsUppercase,
          customFormValidators.containsLowercase, customFormValidators.containsDigit,
          customFormValidators.containsSymbol
          ]
        ],
      }, { validator: this.passwordMatches }),
    });
  }

  passwordMatches(c: AbstractControl): { passwordMatch: boolean } {
    if (c.get('Password').value !== c.get('ConfirmPassword').value) {
      return { passwordMatch: true };
    }
  }

  /**
  *  Decodes url encoded strings
  *
  * @param: string - url or email address we want to decode
  */
  private urldecode(url: string) {
    return decodeURIComponent(url.replace(/\+/g, ' '));
  }

  save() {
    // send info to server and redirect on complete?
    const confirmation = new NewUserConfirmationModel();
    confirmation.Id = this.userId;
    confirmation.UserName = this.name;
    confirmation.ValidationToken = this.validationToken;
    confirmation.Password = this.newUserPasswordForm.value.Passwords.Password;
    this.confirmUser(confirmation);
  }
}
